import generatedCanonicalRedirects from "../../../product-url-redirects.json";

const STOREFRONT_ORIGIN = "https://hooliganseyewear.com";
const STOREFRONT_HOST = /^(?:test\.|www\.)?hooliganseyewear\.com$/i;
const LEGACY_INTERNAL_ROUTE =
  /^(?:\/index\.php(?:\/|$)|\/page\/\d+(?:\/|$)|\/store\/(?:category|manufacturer|product|special)(?:\/|$)|\/blog\/(?:category|post)\/\d+(?:\/|$))/i;

// A backend SEO URL is allowed to carry only real pagination. Context
// (category_id), tracking and catalog-filter parameters must never leak from
// backend payloads into the public link graph.
const BACKEND_QUERY_KEYS = new Set(["page"]);

// These keys are deliberately supported when a component explicitly builds a
// UI route. Keep this list in sync with the catalog router; unknown keys are
// ignored rather than accidentally creating new crawlable URL variants.
const UI_QUERY_KEYS = new Set([
  "page",
  "filters",
  "search",
  "sort",
  "order",
  "category",
]);
const NON_INDEXABLE_QUERY_KEYS = new Set([
  "filters",
  "search",
  "sort",
  "order",
  "size",
  "category",
  "min_price",
  "max_price",
]);

const CANONICAL_REDIRECTS = generatedCanonicalRedirects as Record<
  string,
  string
>;

/**
 * Resolve the exact SEO Orchestrator redirect snapshot bundled into this
 * immutable frontend release. Backend breadcrumbs can still contain the old
 * manufacturer/category keyword after its public canonical has changed; a
 * visible link must point straight at the final 200 URL instead of relying on
 * a redirect.
 */
function resolveBundledCanonicalRedirect(pathname: string): string {
  let current = pathname;
  const seen = new Set<string>();

  while (!seen.has(current)) {
    seen.add(current);
    const destination = CANONICAL_REDIRECTS[current];
    if (typeof destination !== "string") break;

    const normalized = destination.trim().replace(/\/+$/, "") || "/";
    if (!normalized.startsWith("/") || normalized.startsWith("//")) break;
    if (normalized.includes("?") || normalized.includes("#")) break;
    current = normalized;
  }

  return current;
}

/**
 * Convert an OpenCart/VueFront SEO URL into the clean internal path that
 * router-link should publish. Product and manufacturer API URLs occasionally
 * arrive as absolute URLs or with legacy context parameters; neither belongs
 * in the site's internal link graph.
 */
export function toCanonicalInternalPath(value: unknown): string {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return "";

  try {
    const parsed = new URL(raw, STOREFRONT_ORIGIN);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    if (!STOREFRONT_HOST.test(parsed.hostname)) return "";
    const pathname =
      parsed.pathname === "/" ? "/" : parsed.pathname.replace(/\/+$/, "");
    return resolveBundledCanonicalRedirect(pathname || "/");
  } catch {
    return "";
  }
}

export type CanonicalInternalRoute = {
  path: string;
  query?: Record<string, string>;
};

type RouteQueryValue = string | number | boolean | null | undefined;

const normalizedQueryValue = (key: string, value: RouteQueryValue): string => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";

  // Page one is the clean base URL. Invalid and unbounded pagination values
  // are omitted so they cannot create a crawl trap.
  if (key === "page") {
    if (!/^\d+$/.test(normalized)) return "";
    const page = Number(normalized);
    return Number.isSafeInteger(page) && page > 1 ? String(page) : "";
  }

  return normalized;
};

/**
 * Build a Vue Router target from a canonical backend URL. Only `page` may be
 * inherited from that URL. A small explicit allowlist controls component-owned
 * UI parameters; category_id, tracking and unknown keys are always discarded.
 * Returning null keeps a missing/legacy URL from becoming a crawlable link.
 */
export function canonicalInternalRoute(
  value: unknown,
  extraQuery: Record<string, RouteQueryValue> = {}
): CanonicalInternalRoute | null {
  const path = toCanonicalInternalPath(value);
  if (!path || LEGACY_INTERNAL_ROUTE.test(path)) return null;

  const query: Record<string, string> = {};
  try {
    const parsed = new URL(String(value), STOREFRONT_ORIGIN);
    BACKEND_QUERY_KEYS.forEach((key) => {
      const values = parsed.searchParams.getAll(key);
      // Duplicate query keys are ambiguous and should not become crawlable.
      if (values.length !== 1) return;
      const normalized = normalizedQueryValue(key, values[0]);
      if (normalized) query[key] = normalized;
    });
  } catch {
    // `toCanonicalInternalPath` already validated the path. A malformed query
    // should not prevent the clean destination from being used.
  }

  Object.entries(extraQuery).forEach(([key, item]) => {
    if (!UI_QUERY_KEYS.has(key)) return;
    if (item === null || item === undefined || item === "") return;
    const normalized = normalizedQueryValue(key, item);
    if (normalized) query[key] = normalized;
    else delete query[key];
  });

  return Object.keys(query).length > 0 ? { path, query } : { path };
}

/**
 * Return a clean, crawlable path for visible links and structured data.
 * Queries are intentionally omitted; callers that need supported UI state
 * should use canonicalInternalRoute instead.
 */
export function toCrawlableInternalPath(value: unknown): string {
  return canonicalInternalRoute(value)?.path || "";
}

/** True when a router target represents a faceted/non-indexable catalog view. */
export function hasNonIndexableCatalogQuery(value: unknown): boolean {
  const keys: string[] = [];
  if (typeof value === "string") {
    try {
      const parsed = new URL(value, STOREFRONT_ORIGIN);
      keys.push(...Array.from(parsed.searchParams.keys()));
    } catch {
      return false;
    }
  } else if (value && typeof value === "object") {
    const query = (value as { query?: Record<string, unknown> }).query;
    if (query && typeof query === "object") keys.push(...Object.keys(query));
  }

  return keys.some((key) => {
    const normalized = String(key).toLowerCase();
    return (
      NON_INDEXABLE_QUERY_KEYS.has(normalized) ||
      normalized.startsWith("min_filters_") ||
      normalized.startsWith("max_filters_")
    );
  });
}

type ProductLinkSource = {
  id?: unknown;
  name?: unknown;
  url?: unknown;
} | null;

export type CrawlableProductLink = {
  name: string;
  path: string;
};

/**
 * Build the minimal product-link contract shared by catalog markup and
 * ItemList JSON-LD. Requiring both a public SEO URL and a human product name
 * keeps structured data from describing an item that the rendered page cannot
 * expose as a truthful, crawlable link.
 */
export function toCrawlableProductLink(
  product: ProductLinkSource
): CrawlableProductLink | null {
  const path = toCrawlableInternalPath(product?.url);
  const name = String(product?.name ?? "").trim();

  return path && name ? { name, path } : null;
}

/**
 * The product's public SEO path, or "" when the API gave none. OpenCart
 * answers a product without a keyword with its numeric fallback
 * (`/store/product/<id>`, possibly absolute), which the server answers with
 * 404, so that URL counts as "no SEO URL" exactly like an empty one.
 */
export function productSeoPath(product: ProductLinkSource): string {
  const path = toCrawlableInternalPath(product?.url);
  return path && !/^\/\d+$/.test(path) ? path : "";
}

export function hasProductSeoUrl(product: ProductLinkSource): boolean {
  return productSeoPath(product) !== "";
}

/**
 * Prefer the product's canonical SEO URL. The numeric route is retained only
 * for incomplete legacy/localStorage records that genuinely lack canonical
 * data, so a usable card never loses its destination.
 */
export function productCardPath(product: ProductLinkSource): string {
  const canonicalPath = toCanonicalInternalPath(product?.url);
  if (canonicalPath) return canonicalPath;

  const id = String(product?.id ?? "").trim();
  return id ? `/store/product/${encodeURIComponent(id)}` : "/designer-eyewear";
}
